import React, { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronRight } from 'lucide-react'
import PersonCard from './PersonCard'
import type { RankedPersonItem } from '../../hooks/useRankedPeople'
import type { CollectionItem } from '../../types/collections'

// Ranked-rail UX constants. `RANKED_DEFAULT_COUNT` is the initial slice when
// the rail is expanded; "Show more" extends to `RANKED_MAX_COUNT`. Server
// returns up to `RANKED_MAX_COUNT` on each rail in one round-trip.
const RANKED_DEFAULT_COUNT = 4
export const RANKED_MAX_COUNT = 10

/**
 * One ranked horizontal rail (Recent / Closest). Header is clickable to
 * collapse; when expanded, shows the first `RANKED_DEFAULT_COUNT` people with
 * a "Show more" affordance that extends to `RANKED_MAX_COUNT`.
 *
 * Hidden entirely (header included) when `people` is empty — the search
 * behavior we picked says ranked rails should disappear under a non-matching
 * filter rather than render an empty header.
 *
 * Action callbacks are optional: passing none renders the cards in view-only
 * mode (no edit/delete/checkins row). Used by CollectionsPage to render a
 * read-only preview of the rails.
 */
interface RankedRailProps {
    title: string
    subtitle: string
    people: RankedPersonItem[]
    onView: (p: RankedPersonItem) => void
    onEdit?: (p: RankedPersonItem) => void
    onCheckins?: (p: RankedPersonItem) => void
    onDelete?: (p: RankedPersonItem) => void
}

const RankedRail: React.FC<RankedRailProps> = ({
    title,
    subtitle,
    people,
    onView,
    onEdit,
    onCheckins,
    onDelete,
}) => {
    const [expanded, setExpanded] = useState(true)
    const [showAll, setShowAll] = useState(false)

    if (people.length === 0) return null

    const visible = showAll
        ? people.slice(0, RANKED_MAX_COUNT)
        : people.slice(0, RANKED_DEFAULT_COUNT)
    const moreAvailable = people.length > RANKED_DEFAULT_COUNT && !showAll

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left cursor-pointer"
                aria-expanded={expanded}
            >
                <motion.span
                    animate={{ rotate: expanded ? 90 : 0 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    className="inline-flex"
                >
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                </motion.span>
                <span className="font-semibold text-gray-800">{title}</span>
                <span className="text-xs text-gray-500">{subtitle}</span>
                <span className="ml-auto text-xs text-gray-400">{people.length}</span>
            </button>
            <AnimatePresence initial={false}>
                {expanded && (
                    <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        style={{ overflow: 'hidden' }}
                    >
                        <div className="px-3 pb-3">
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                <AnimatePresence initial={false}>
                                    {visible.map((p) => (
                                        <motion.div
                                            key={p.collection_item_id}
                                            layout
                                            style={{ transformOrigin: 'left center' }}
                                            initial={{ opacity: 0, scale: 0.6 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.6 }}
                                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                        >
                                            <PersonCard
                                                person={resolveCardItem(p)}
                                                checkinCount={p.checkin_count}
                                                onView={() => onView(p)}
                                                onEdit={onEdit ? () => onEdit(p) : undefined}
                                                onCheckins={onCheckins ? () => onCheckins(p) : undefined}
                                                onDelete={onDelete ? () => onDelete(p) : undefined}
                                            />
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            </div>
                            {moreAvailable && (
                                <div className="mt-2 text-right">
                                    <motion.button
                                        type="button"
                                        onClick={() => setShowAll(true)}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                        className="text-xs text-amber-800 hover:text-amber-900 px-2 py-1 cursor-pointer"
                                    >
                                        Show more ({Math.min(people.length, RANKED_MAX_COUNT) - RANKED_DEFAULT_COUNT})
                                    </motion.button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

// Build a minimal CollectionItem-shaped object for `PersonCard` from a
// ranked rail row. The rail row already carries the full `data` payload,
// and PersonCard only reads `data` to render — full-functionality callers
// route the actions through the real CollectionItem via their own resolver.
function resolveCardItem(p: RankedPersonItem): CollectionItem {
    return { id: p.collection_item_id, data: p.data } as CollectionItem
}

export default RankedRail
